/**
 * @ Author: Housefolios
 * @ Create Time: 2022-05-13 13:51:02
 * @ Modified by: David Helmick
 * @ Modified time: 2025-03-03 13:32:24
 * @ Description: Dialog to remove options from dropdowns
 */

import React, { useState } from 'react'

// Centralized lodash imports - much cleaner!
import {
  remove,
  pull,
  findIndex,
  isNil,
  cloneDeep,
} from '@/utils/lodash'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Box,
} from '@mui/material'
import { useMutation } from '@apollo/client/react'
import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { enqueueSnackbar } from 'notistack'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import withSession from '@/housefolios-components/Session/withSession'
import { UPDATE_LOT } from '@/housefolios-components/Lots/mutations'
import { UPDATE_PROPERTIES } from '@/housefolios-components/Portfolio/mutations'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_LOT } from '@/housefolios-components/Lots/queries'
import { DeleteOutlined } from '@mui/icons-material'
import { UPDATE_ORGANIZATION } from '@/housefolios-components/OrganizationSettings/mutations'
import { UPDATE_AFFILIATE } from '@/housefolios-components/PartnerPortal/mutations'
import useAffiliate from '@/hooks/useAffiliate'
import { GET_AFFILIATE } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'

function RemoveOptionComponent(props) {
  const {
    removeOption,
    removeOptionLabel,
    removeOptionValue,
    removeOptionItemLabel,
  } = props
  const [loader, setLoader] = useState(false)
  const [openRemoveOption, setOpenRemoveOption] = useState(false)
  const [removeOptionConfirm, setRemoveOptionConfirm] =
    useState(false)

  const handleOpenRemoveOption = (event) => {
    setOpenRemoveOption(true)
    setRemoveOptionConfirm(false)
    setLoader(false)
    event.stopPropagation()
  }

  const handleCloseRemoveOption = () => {
    setOpenRemoveOption(false)
    setRemoveOptionConfirm(false)
    setLoader(false)
  }

  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)
  const [updateAffiliate] = useMutation(UPDATE_AFFILIATE)

  const [{ affiliate }] = useAffiliate({
    memberValue: props?.session?.me?.activeOrg?.member,
  })

  return (
    <>
      {props.menuItem ? (
        <MenuItem
          key={removeOptionValue}
          value={removeOptionValue}
          onClick={handleOpenRemoveOption}
        >
          <Grid
            container
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid data-hf-grid="keep">{removeOptionLabel}</Grid>
          </Grid>
        </MenuItem>
      ) : (
        <Grid>
          <IconButton onClick={handleOpenRemoveOption} size="small">
            <DeleteOutlined fontSize="medium" />
          </IconButton>
        </Grid>
      )}

      <Dialog
        open={openRemoveOption}
        onClose={handleCloseRemoveOption}
        onClick={(event) => event.stopPropagation()}
        //maxWidth="xs"
        fullWidth={true}
      >
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>
              Deleting {removeOptionLabel}
            </Box>
          }
        >
          <Box sx={{ textAlign: 'center', p: '3rem' }}>
            <Box sx={[{ borderRadius: '50%', m: 0 }, {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "0.25rem",
              position: "relative",
              fontSize: "1.2rem",
              transition: "all 0.2s ease-in-out",
              "&:last-child": { mr: 0, marginRight: 0 },
              "& .badge": { position: "absolute", right: 0, bottom: 0 },
              "& svg, & .svg-inline--fa": { display: "block" },
              marginRight: "0.25rem",
            }]}>
              <Box sx={[{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: 0,
                backgroundColor: "rgb(100%, 95.9215686275%, 96.418938307%) !important",
                height: "130px !important",
                width: "130px !important",
                lineHeight: "130px !important",
                p: 0,
                borderRadius: "50%",
                color: "#f83245",
                m: 0,
              }, {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mr: "0.25rem",
                position: "relative",
                fontSize: "1.2rem",
                transition: "all 0.2s ease-in-out",
                "&:last-child": { mr: 0, marginRight: 0 },
                "& .badge": { position: "absolute", right: 0, bottom: 0 },
                "& svg, & .svg-inline--fa": { display: "block" },
                marginRight: "0.25rem",
              }]}>
                <FontAwesomeIcon icon={['far', 'trash-can']} style={{ display: 'flex', alignSelf: 'center' }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
              </Box>
            </Box>
            <Box
              component="h4"
              sx={{ fontWeight: 700, mt: '1.5rem' }}
            >
              Delete {removeOptionLabel}?
            </Box>
            <Box sx={[{ mb: 0, color: '#a6a6b9' }, { fontSize: "1.15rem !important" }]}>
              <FormControl component="fieldset" sx={{ pr: '1.5rem' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={removeOptionConfirm}
                        onChange={() =>
                          setRemoveOptionConfirm(!removeOptionConfirm)
                        }
                      />
                    }
                    label={`Delete ${removeOptionLabel} - ${
                      removeOptionItemLabel || removeOptionValue
                    }. This action can't be undone`}
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box sx={{ pt: '1.5rem' }}>
              <Button
                variant="text"
                onClick={handleCloseRemoveOption}
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Cancel</Box>
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: '#f83145', color: 'white' }}
                onClick={() => {
                  const { activeOrg } = props.session.me
                  setLoader(true)
                  let newOptions = props.isAffiliate
                    ? cloneDeep(affiliate[removeOption]) || []
                    : cloneDeep(activeOrg[removeOption]) || []
                  if (removeOption === 'rehabTiers') {
                    const index = findIndex(newOptions, (n) => {
                      return (
                        n.value === removeOptionValue &&
                        (!removeOptionItemLabel ||
                          n.label === removeOptionItemLabel ||
                          n.title === removeOptionItemLabel)
                      )
                    })
                    if (index >= 0) {
                      // This should make it trigger between true and false with undefined being treated as true
                      // undefined = false == true = false
                      // false = true == true = true
                      // true = true == false = false
                      newOptions[index].active =
                        !isNil(newOptions[index].active) ==
                        !newOptions[index].active
                    }
                  } else {
                    pull(newOptions, removeOptionValue)
                    remove(
                      newOptions,
                      (n) =>
                        n.value === removeOptionValue &&
                        (!removeOptionItemLabel ||
                          n.label === removeOptionItemLabel ||
                          n.title === removeOptionItemLabel),
                    )
                  }
                  let organizationInput = {}
                  organizationInput[removeOption] = newOptions
                  if (props.isAffiliate) {
                    updateAffiliate({
                      variables: {
                        affiliateId: affiliate._id,
                        affiliateInput: organizationInput,
                      },
                      refetchQueries: [
                        {
                          query: GET_ME,
                        },
                        {
                          query: GET_AFFILIATE,
                          variables: {
                            memberValue: affiliate?.memberValue,
                          },
                        },
                      ],
                    })
                      .then(() => {
                        enqueueSnackbar(`Option has been removed`, {
                          variant: 'success',
                          autoHideDuration: 3000,
                        })
                        handleCloseRemoveOption()
                      })
                      .catch(() => {
                        enqueueSnackbar(
                          `Option has failed to be removed`,
                          {
                            variant: 'error',
                            autoHideDuration: 3000,
                          },
                        )
                        handleCloseRemoveOption()
                      })
                  } else {
                    updateOrganization({
                      variables: {
                        id: activeOrg._id,
                        organizationInput,
                      },
                      refetchQueries: [
                        {
                          query: GET_ME,
                        },
                      ],
                    })
                      .then(() => {
                        enqueueSnackbar(`Option has been removed`, {
                          variant: 'success',
                          autoHideDuration: 3000,
                        })
                        handleCloseRemoveOption()
                      })
                      .catch(() => {
                        enqueueSnackbar(
                          `Option has failed to be removed`,
                          {
                            variant: 'error',
                            autoHideDuration: 3000,
                          },
                        )
                        handleCloseRemoveOption()
                      })
                  }
                }}
                disabled={!removeOptionConfirm || loader}
                href={props.gotToPortfolio ? '/my-properties' : null}
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Delete</Box>
              </Button>
            </Box>
          </Box>
        </BlockUi>
      </Dialog>
    </>
  )
}

export default withSession(RemoveOptionComponent)
